import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_REPORT_LANGUAGES,
  ALL_RESULT_TEMPLATES_FOR_SAMPLE,
  ERROR_CODES,
  REPORT_SAMPLE_GENERAL,
  REPORT_TEMPLATE_LAYOUT,
  REPORT_TEMPLATE_STATUS,
  pickBilingual,
  type Locale,
  type PersonRef,
  type ReportLanguage,
} from '@lims/shared-types';
import {
  createReportTemplateSchema,
  updateReportTemplateSchema,
} from '@lims/shared-validation';
import { ApiError } from '../../api/client.js';
import {
  isRenderingUnavailable,
  reportTemplatesApi,
  reportsApi,
  workOrdersApi,
  type ReportTemplateView,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatDateTime } from '../../components/format.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  BilingualTextField,
  CheckboxGroupField,
  Dialog,
  Panel,
  RadioField,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  SwitchField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * Pieces shared by the report-template register and a template's own page.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 6.1 AND 8 BEFORE ADDING ANYTHING HERE.
 * ===========================================================================
 *
 * A template is the laboratory's own configuration (D112). It holds a name, the
 * languages a report may be issued in, and the ONE layout that exists —
 * `general`, which prints the general result exactly as recorded. There is no
 * field here for a table, a chart, a calculation, a unit or a limit, because
 * every one of those is test science and is gated on a validated worked example.
 *
 * Every action below is shown only to a holder of `templates.manage`, and every
 * one of them is re-checked by the server — hiding a button is presentation,
 * never protection (CLAUDE.md section 5.1).
 */

/** The query key every template read lives under, so one save refreshes all. */
export const TEMPLATES_QUERY_KEY = ['report-templates'] as const;

/** Detail codes the template endpoints send with a 409 (Agent T's mapping). */
export const TEMPLATE_RETIRED = 'template_retired';
export const DEFAULT_CHANGED_CONCURRENTLY = 'default_changed_concurrently';

/** Reasons shorter than this are refused by the shared `reasonSchema`. */
const MINIMUM_REASON = 3;
/** The shared schema's limit on each half of a template's name. */
const MAXIMUM_NAME = 200;

/** The template's name in the reader's language, or the other one as entered. Never translated. */
export function templateName(
  template: Pick<ReportTemplateView, 'name'>,
  locale: Locale,
): string {
  return pickBilingual(template.name, locale) ?? '';
}

/**
 * A placeholder for an interpolated run, invisible and never found in a
 * catalogue sentence, so the sentence can be split around it.
 */
export function run(name: string): string {
  return `⁣${name}⁣`;
}

/**
 * Render a translated sentence with each interpolated run isolated in `<bdi>`.
 *
 * "Created by {{name}} on {{date}}" in Arabic, with a Latin name and an
 * Arabic-formatted date, is exactly the case where the bidirectional algorithm
 * pulls the date's digits into the name's run. Isolating each run keeps both
 * readable, and no run is forced to `dir="ltr"` — an Arabic date must stay an
 * Arabic date.
 */
export function isolateRuns(
  sentence: string,
  runs: Readonly<Record<string, ReactNode>>,
): ReactNode {
  return sentence.split(/⁣(\w+)⁣/).map((part, index) =>
    index % 2 === 1 ? (
      <bdi key={index}>{runs[part]}</bdi>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

/** "Created by NAME on DATE", with both runs isolated. */
export function ActedBy({
  sentenceKey,
  person,
  at,
}: {
  readonly sentenceKey:
    | 'reportTemplates.createdBy'
    | 'reportTemplates.updatedBy'
    | 'reportTemplates.retiredBy';
  readonly person: PersonRef | null;
  readonly at: string | null;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const runs = {
    name: person?.displayName ?? t('common.notRecorded'),
    date: formatDateTime(at, { locale }) ?? t('common.notRecorded'),
  };
  const placeholders = { name: run('name'), date: run('date') };
  const sentence =
    sentenceKey === 'reportTemplates.createdBy'
      ? t('reportTemplates.createdBy', placeholders)
      : sentenceKey === 'reportTemplates.updatedBy'
        ? t('reportTemplates.updatedBy', placeholders)
        : t('reportTemplates.retiredBy', placeholders);
  return <>{isolateRuns(sentence, runs)}</>;
}

/** Active or withdrawn, and whether it is the default. Mark, word and colour. */
export function TemplateBadges({ template }: { readonly template: ReportTemplateView }) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap items-center gap-inline">
      <StatusBadge
        label={t(`reportTemplateStatus.${template.status}`)}
        tone={template.status === REPORT_TEMPLATE_STATUS.ACTIVE ? 'success' : 'neutral'}
      />
      {template.isDefault ? (
        <StatusBadge label={t('reportTemplates.defaultBadge')} tone="info" />
      ) : null}
    </span>
  );
}

/** The languages a template supports, in the product's fixed order. */
export function LanguageList({ languages }: { readonly languages: readonly ReportLanguage[] }) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap gap-inline">
      {ALL_REPORT_LANGUAGES.filter((language) => languages.includes(language)).map(
        (language) => (
          // Plain words, not a status badge: a language is a fact, not a state.
          <span
            key={language}
            className="rounded-small border border-default-200 px-1.5 text-caption text-default-800"
          >
            {t(`reportLanguage.${language}`)}
          </span>
        ),
      )}
    </span>
  );
}

/**
 * A refusal from a template endpoint, in words.
 *
 * A specific refusal the catalogue knows (a withdrawn template, say) is named;
 * anything else falls back to the stable error code's message. Never the
 * server's own English.
 */
export function TemplateErrorNotice({ error }: { readonly error: unknown }) {
  const { t, i18n } = useTranslation();
  if (!error) return null;
  const codes =
    error instanceof ApiError ? error.details.map((detail) => detail.code) : [];

  // A change to a template that was withdrawn meanwhile (409
  // INVALID_STATE_TRANSITION). On THIS page the act refused is an edit, a
  // default or a withdrawal — not preparing a report — so it is said that way.
  if (codes.includes(TEMPLATE_RETIRED)) {
    return <Alert tone="danger" title={t('reportTemplates.editOnlyActive')} />;
  }
  // Two managers setting a default at the same moment (409 CONFLICT). Nothing
  // changed for this one, and the page says so rather than claiming success.
  if (codes.includes(DEFAULT_CHANGED_CONCURRENTLY)) {
    return (
      <Alert tone="danger" title={t('reportTemplates.defaultChangedConcurrently')} />
    );
  }

  const refusal = codes.find(
    (code): code is string =>
      typeof code === 'string' && i18n.exists(`reports.refusal.${code}`),
  );
  if (refusal) {
    return <Alert tone="danger" title={t(`reports.refusal.${refusal}`)} />;
  }
  return <ErrorNotice error={error} />;
}

// ---------------------------------------------------------------------------
// Create and edit
// ---------------------------------------------------------------------------

export function TemplateFormDialog({
  template,
  onClose,
}: {
  /** `null` creates a new template. */
  readonly template: ReportTemplateView | null;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [nameAr, setNameAr] = useState(template?.name.ar ?? '');
  const [nameEn, setNameEn] = useState(template?.name.en ?? '');
  const [languages, setLanguages] = useState<string[]>(
    template ? [...template.languages] : [],
  );
  const [isDefault, setIsDefault] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const trimmedAr = nameAr.trim();
  const trimmedEn = nameEn.trim();
  const nameMissing = trimmedAr === '' && trimmedEn === '';
  const nameErrorAr = nameMissing
    ? t('validation.required')
    : trimmedAr.length > MAXIMUM_NAME
      ? t('validation.tooLong')
      : undefined;
  const nameErrorEn = nameMissing
    ? t('validation.required')
    : trimmedEn.length > MAXIMUM_NAME
      ? t('validation.tooLong')
      : undefined;
  const languagesError =
    languages.length === 0 ? t('validation.required') : undefined;

  const orderedLanguages = ALL_REPORT_LANGUAGES.filter((language) =>
    languages.includes(language),
  );
  const name = { ar: trimmedAr || null, en: trimmedEn || null };

  const save = useMutation({
    mutationFn: () =>
      template
        ? reportTemplatesApi.update(template.id, { name, languages: orderedLanguages })
        : reportTemplatesApi.create({
            name,
            languages: orderedLanguages,
            ...(isDefault ? { isDefault: true } : {}),
          }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
      // Raised from the SERVER's confirmation, never from the button press.
      show({
        tone: 'success',
        message: template ? t('reportTemplates.saved') : t('reportTemplates.created'),
      });
      onClose();
      // A new template opens on its own page, where its sections are chosen
      // and previewed — the dialog only names it.
      if (!template) navigate(`/report-templates/${saved.id}`);
    },
  });

  const submit = () => {
    setShowErrors(true);
    if (nameErrorAr || nameErrorEn || languagesError) return;
    // The same schema the API validates with. A body it refuses is never sent.
    const body = template
      ? { name, languages: orderedLanguages }
      : { name, languages: orderedLanguages, ...(isDefault ? { isDefault: true } : {}) };
    const valid = template
      ? updateReportTemplateSchema.safeParse(body).success
      : createReportTemplateSchema.safeParse(body).success;
    if (!valid) return;
    save.mutate();
  };

  const hasErrors = showErrors && Boolean(nameErrorAr || nameErrorEn || languagesError);

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={template ? t('reportTemplates.editTitle') : t('reportTemplates.createTitle')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={save.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton priority="primary" isLoading={save.isPending} onPress={submit}>
            {t('common.save')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        {hasErrors ? (
          <Alert
            tone="warning"
            title={t('validation.summaryTitle')}
            description={t('validation.summaryDescription')}
          />
        ) : null}
        <div className="flex flex-col gap-inline">
          <BilingualTextField
            labelAr={t('reportTemplates.nameAr')}
            labelEn={t('reportTemplates.nameEn')}
            valueAr={nameAr}
            valueEn={nameEn}
            onChangeAr={setNameAr}
            onChangeEn={setNameEn}
            {...(showErrors && nameErrorAr ? { errorAr: nameErrorAr } : {})}
            {...(showErrors && nameErrorEn ? { errorEn: nameErrorEn } : {})}
          />
          <p className="text-caption text-default-600">{t('reportTemplates.nameHint')}</p>
        </div>
        <CheckboxGroupField
          label={t('reportTemplates.languages')}
          description={t('reportTemplates.languagesHint')}
          value={languages}
          onChange={setLanguages}
          isRequired
          {...(showErrors && languagesError ? { error: languagesError } : {})}
          options={ALL_REPORT_LANGUAGES.map((language) => ({
            value: language,
            label: t(`reportLanguage.${language}`),
          }))}
        />
        {/* Shown, never chosen: one layout exists, and it cannot change later. */}
        <ReadOnlyField label={t('reportTemplates.layout')}>
          {t(`reportTemplateLayout.${template?.layout ?? REPORT_TEMPLATE_LAYOUT.GENERAL}`)}
        </ReadOnlyField>
        <p className="-mt-2 text-caption text-default-600">
          {t(template ? 'reportTemplates.layoutHint' : 'reportTemplates.createLayoutHint')}
        </p>
        {template ? null : (
          <SwitchField
            label={t('reportTemplates.isDefault')}
            description={t('reportTemplates.defaultHint')}
            value={isDefault}
            onChange={setIsDefault}
          />
        )}
        {save.error ? <TemplateErrorNotice error={save.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Withdraw from use
// ---------------------------------------------------------------------------

export function RetireTemplateDialog({
  template,
  onClose,
}: {
  readonly template: ReportTemplateView;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const reasonReady = reason.trim().length >= MINIMUM_REASON;

  const retire = useMutation({
    mutationFn: () => reportTemplatesApi.retire(template.id, { reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
      show({ tone: 'success', message: t('reportTemplates.retired') });
      onClose();
    },
  });

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('reportTemplates.retire')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={retire.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="destructive"
            // The reason is mandatory here AND on the server, which refuses a
            // withdrawal without one whatever this button does.
            isDisabled={!reasonReady}
            isLoading={retire.isPending}
            onPress={() => retire.mutate()}
          >
            {t('reportTemplates.retire')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <p className="font-medium text-default-900">
          <bdi>{templateName(template, locale)}</bdi>
        </p>
        <Alert
          tone="warning"
          title={t('reportTemplates.retireWarningTitle')}
          description={t('reportTemplates.retireWarningBody')}
        />
        <ReadOnlyField label={t('reportTemplates.issuedVersionCount')}>
          <span className="tabular-nums">{template.issuedVersionCount}</span>
        </ReadOnlyField>
        <p className="-mt-2 text-caption text-default-600">
          {t('reportTemplates.issuedVersionCountHint')}
        </p>
        <TextAreaField
          label={t('reportTemplates.retireReason')}
          value={reason}
          onChange={(value) => {
            setReason(value);
            setTouched(true);
          }}
          isRequired
          rows={3}
          {...(touched && !reasonReady
            ? {
                error:
                  reason.trim() === '' ? t('validation.required') : t('validation.tooShort'),
              }
            : {})}
        />
        {retire.error ? <TemplateErrorNotice error={retire.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// The manage actions, wherever a template is shown
// ---------------------------------------------------------------------------

/**
 * Edit, set as default and withdraw — for a holder of `templates.manage`, on an
 * ACTIVE template. A withdrawn template has no action: it is terminal.
 */
export function TemplateManageActions({
  template,
  size = 'sm',
}: {
  readonly template: ReportTemplateView;
  readonly size?: 'sm' | 'md';
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'edit' | 'retire' | null>(null);

  const setDefault = useMutation({
    mutationFn: () => reportTemplatesApi.setDefault(template.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
      show({ tone: 'success', message: t('reportTemplates.defaultSet') });
    },
  });

  if (!can(PERMISSIONS.TEMPLATES_MANAGE)) return null;
  if (template.status !== REPORT_TEMPLATE_STATUS.ACTIVE) return null;

  return (
    <div className="flex flex-col items-end gap-inline">
      <div className="flex flex-wrap justify-end gap-inline">
        <ActionButton priority="tertiary" size={size} onPress={() => setDialog('edit')}>
          {t('reportTemplates.edit')}
        </ActionButton>
        {template.isDefault ? null : (
          <ActionButton
            priority="secondary"
            size={size}
            isLoading={setDefault.isPending}
            onPress={() => setDefault.mutate()}
          >
            {t('reportTemplates.setDefault')}
          </ActionButton>
        )}
        <ActionButton priority="destructive" size={size} onPress={() => setDialog('retire')}>
          {t('reportTemplates.retire')}
        </ActionButton>
      </div>
      {setDefault.error ? <TemplateErrorNotice error={setDefault.error} /> : null}
      {dialog === 'edit' ? (
        <TemplateFormDialog template={template} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'retire' ? (
        <RetireTemplateDialog template={template} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------


/**
 * The template preview (D127, D255): a real A4 rendering shown in the page.
 *
 * Two sources. A SAMPLE — the laboratory's real header with placeholders that
 * say where each value is printed, never a value, and the outline of a chosen
 * test's results area. Or ONE EXISTING APPROVED RESULT printed through this
 * template exactly as a report would print it now, marked PREVIEW — offered
 * only to someone who prepares or issues reports, and re-checked by the
 * server. Nothing is numbered, stored or recorded either way.
 */
export function TemplatePreviewPanel({ template }: { readonly template: ReportTemplateView }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();
  const mayUseResults = can(PERMISSIONS.REPORTS_PREPARE) || can(PERMISSIONS.REPORTS_ISSUE);
  const [source, setSource] = useState<'sample' | 'result'>('sample');
  const [sampleArea, setSampleArea] = useState<string>(REPORT_SAMPLE_GENERAL);
  const [workOrderId, setWorkOrderId] = useState<string | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [shown, setShown] = useState<{ url: string; language: ReportLanguage } | null>(null);

  // The object URL of the shown PDF is released when it is replaced or the page closes.
  useEffect(
    () => () => {
      if (shown?.url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(shown.url);
    },
    [shown],
  );

  const workOrders = useQuery({
    queryKey: ['work-orders', 'template-preview'],
    queryFn: () => workOrdersApi.list({ pageSize: 100 }),
    enabled: source === 'result' && mayUseResults,
  });
  const eligible = useQuery({
    queryKey: ['reports', 'eligible', 'template-preview', workOrderId],
    queryFn: () => reportsApi.eligibleResults(workOrderId as string),
    enabled: source === 'result' && workOrderId !== null,
  });

  const preview = useMutation({
    mutationFn: async (language: ReportLanguage) => {
      const blob = await reportTemplatesApi.preview(
        template.id,
        source === 'result' && attemptId
          ? { language, source: 'result', attemptId }
          : { language, source: 'sample', sampleResultTemplate: sampleArea },
      );
      // Anything but a PDF is not a preview, whatever answered the request.
      if (!blob.type.startsWith('application/pdf')) {
        throw new ApiError(ERROR_CODES.INTERNAL_ERROR, 0, 'The preview was not a PDF.');
      }
      const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : '';
      setShown({ url, language });
      return language;
    },
  });

  const supported = ALL_REPORT_LANGUAGES.filter((language) => template.languages.includes(language));
  const needsResult = source === 'result' && attemptId === null;
  const results = eligible.data?.data ?? [];

  return (
    <Panel title={t('reportTemplates.preview')} description={t('reportTemplates.previewHint')}>
      <div className="flex flex-col gap-stack">
        <p className="text-caption text-default-600">{t('reportTemplates.previewHeaderHint')}</p>
        {mayUseResults ? (
          <RadioField
            label={t('reportTemplates.previewSource')}
            orientation="horizontal"
            value={source}
            onChange={(value) => setSource(value === 'result' ? 'result' : 'sample')}
            options={[
              { value: 'sample', label: t('reportTemplates.sourceSample') },
              { value: 'result', label: t('reportTemplates.sourceResult') },
            ]}
          />
        ) : null}
        {source === 'sample' ? (
          <SelectField
            label={t('reportTemplates.sampleResults')}
            value={sampleArea}
            onChange={(value) => setSampleArea(value ?? REPORT_SAMPLE_GENERAL)}
            isRequired
            options={ALL_RESULT_TEMPLATES_FOR_SAMPLE.map((key) => ({
              value: key,
              label:
                key === REPORT_SAMPLE_GENERAL
                  ? t('reportTemplates.sampleGeneral')
                  : t(`catalogue.templateKey.${key}`),
            }))}
          />
        ) : (
          <div className="grid gap-stack sm:grid-cols-2">
            <SelectField
              label={t('reportTemplates.previewWorkOrder')}
              placeholder={t('common.notSet')}
              value={workOrderId}
              isLoading={workOrders.isLoading}
              onChange={(value) => {
                setWorkOrderId(value);
                setAttemptId(null);
              }}
              options={(workOrders.data?.data ?? []).map((order) => ({
                value: order.id,
                label: [order.workOrderNumber, order.clientName].filter(Boolean).join(' · '),
              }))}
            />
            <SelectField
              label={t('reportTemplates.previewResult')}
              description={t('reportTemplates.previewResultHint')}
              placeholder={t('common.notSet')}
              value={attemptId}
              isDisabled={workOrderId === null}
              isLoading={eligible.isLoading}
              onChange={setAttemptId}
              options={results.map((result) => ({
                value: result.attemptId,
                label: [pickBilingual(result.test.name, locale as Locale), result.sample.number]
                  .filter(Boolean)
                  .join(' · '),
              }))}
            />
            {workOrderId !== null && eligible.isSuccess && results.length === 0 ? (
              <p className="text-caption text-default-600 sm:col-span-2">
                {t('reportTemplates.previewNoResults')}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex flex-wrap gap-inline">
          {supported.map((language) => (
            <ActionButton
              key={language}
              priority="secondary"
              size="sm"
              isLoading={preview.isPending && preview.variables === language}
              isDisabled={needsResult || (preview.isPending && preview.variables !== language)}
              onPress={() => preview.mutate(language)}
            >
              {language === 'ar' ? t('reportTemplates.previewArabic') : t('reportTemplates.previewEnglish')}
            </ActionButton>
          ))}
        </div>
        {needsResult ? (
          <p className="text-caption text-default-600">{t('reportTemplates.previewChooseResult')}</p>
        ) : null}
        <p className="text-caption text-default-600">{t('reportTemplates.previewLanguageHint')}</p>
        {preview.isPending ? (
          <p className="text-caption text-default-600" aria-live="polite">
            {t('reportTemplates.previewPreparing')}
          </p>
        ) : null}
        {preview.error ? (
          isRenderingUnavailable(preview.error) ? (
            <Alert tone="warning" title={t('reportTemplates.previewUnavailable')} />
          ) : (
            <TemplateErrorNotice error={preview.error} />
          )
        ) : null}
        {shown?.url ? (
          <div className="flex flex-col gap-inline" data-testid="template-preview-frame">
            {/* A4 is 210 x 297 mm: the frame keeps the page's own proportions. */}
            <iframe
              title={t('reportTemplates.previewFrame')}
              src={shown.url}
              className="aspect-[210/297] w-full rounded-medium border border-default-200 bg-white"
            />
            <a
              href={shown.url}
              target="_blank"
              rel="noopener"
              className="text-caption text-primary-700 underline"
            >
              {t('reportTemplates.previewOpenInTab')}
            </a>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
